/**
 * Auth state: the Google ID token + the signed-in user's profile/permissions.
 *
 * The token (a Google ID JWT) is sent as `Authorization: Bearer <token>` on
 * every API call (see lib/apiClient.ts). The backend verifies it and returns
 * the user's role + accessible events via GET /me.
 */
import { create } from 'zustand';

export type Role = 'admin' | 'member';

export interface Me {
  email: string;
  name: string;
  role: Role;
  /** Event names the user may access; null for admins (= all). */
  events: string[] | null;
}

const TOKEN_KEY = 'gid_token';

/**
 * Epoch-seconds expiry from a Google ID token (JWT `exp` claim), or 0 if it
 * can't be read. Google ID tokens live ~1h; we use this to avoid firing a
 * doomed /me with an already-expired token (which 401s → sign-out).
 */
function tokenExp(token: string): number {
  try {
    const [, payload] = token.split('.');
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp : 0;
  } catch {
    return 0;
  }
}

/** True if the token is missing or past (or within 10s of) its expiry. */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  const exp = tokenExp(token);
  return exp === 0 || exp * 1000 <= Date.now() + 10_000;
}

/** Read a non-expired token from storage, clearing it if stale. */
function loadValidToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored && isTokenExpired(stored)) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
  return stored;
}

interface AuthState {
  /** Google ID token (JWT), or null when signed out. */
  token: string | null;
  /** Profile from GET /me; null until loaded. */
  me: Me | null;
  /** True while the initial /me lookup is in flight. */
  loading: boolean;
  setToken: (token: string | null) => void;
  setMe: (me: Me | null) => void;
  setLoading: (loading: boolean) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  token: loadValidToken(),
  me: null,
  loading: false,
  setToken: (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
    set({ token });
  },
  setMe: (me) => set({ me }),
  setLoading: (loading) => set({ loading }),
  signOut: () => {
    localStorage.removeItem(TOKEN_KEY);
    set({ token: null, me: null });
  },
}));

/** Non-reactive token accessor for the API layer. */
export function getToken(): string | null {
  return useAuthStore.getState().token;
}

/** Called by the API layer on a 401 to force re-authentication. */
export function onUnauthorized(): void {
  useAuthStore.getState().signOut();
}
